import { getInstance, loadRemote as loadFederatedRemote } from '@module-federation/runtime';
import {
  createElement,
  Suspense,
  useEffect,
  useReducer,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';

import { ErrorBoundary } from '@/components/error-boundary';
import { GlobalLoading } from '@/components/loading';

import { hasLocalRemote, loadLocalRemote } from './local-remotes';
import type { RemoteModule } from './types';

export async function loadTenantRemote<TProps extends object = Record<string, never>>(
  remoteName: string,
): Promise<RemoteModule<TProps>> {
  try {
    // 在远程入口初始化前固定宿主实例。远程加载后运行时会新增 remote 实例，
    // 若继续使用全局 current instance，可能选中不含 remotes 的子实例并永久停在 Suspense。
    const hostRuntime = getInstance(instance => instance.name === 'tenant-web');
    const mod = await (hostRuntime
      ? hostRuntime.loadRemote(remoteName)
      : loadFederatedRemote(remoteName));
    if (!mod || typeof mod !== 'object' || !('default' in mod)) {
      throw new Error(`远程模块 ${remoteName} 缺少默认导出`);
    }
    const remoteComponent = mod.default as ComponentType<TProps>;
    // 联邦模块可能默认导出 memo/observer 等 exotic component；用普通函数包一层，
    // 同时隔离运行时模块代理，保证 React.lazy 始终拿到稳定的函数组件。
    return {
      default: function FederatedRemoteComponent(props: TProps) {
        return createElement(remoteComponent, props);
      },
    };
  } catch (error) {
    if (hasLocalRemote(remoteName)) {
      // 内置模块只承担明确的可用性降级，不得在远程失败时绕过权限加载真实业务页。
      return loadLocalRemote(remoteName) as Promise<RemoteModule<TProps>>;
    }
    throw error;
  }
}

interface RemoteBoundaryProps {
  children?: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
}

function RemoteBoundary({ children, fallback, onRetry }: RemoteBoundaryProps) {
  const suspense = createElement(Suspense, {
    children,
    fallback: fallback ?? createElement(GlobalLoading),
  });
  return createElement(ErrorBoundary, { children: suspense, onRetry });
}

function RemoteLoadFailure({ error }: { error: Error }): never {
  throw error;
}

/** createRemoteComponent 将联邦模块请求封装为带 Suspense、旧版错误页和可恢复重试的宿主组件。 */
export function createRemoteComponent<TProps extends object = Record<string, never>>(
  remoteName: string,
): ComponentType<TProps> {
  return function TenantRemoteComponent(props: TProps) {
    const [revision, retry] = useReducer(value => value + 1, 0);
    const [state, setState] = useState<{
      component?: ComponentType<TProps>;
      error?: Error;
    }>({});

    useEffect(() => {
      let active = true;
      setState({});
      void loadTenantRemote<TProps>(remoteName)
        .then((mod) => {
          if (active) setState({ component: mod.default });
        })
        .catch((error: unknown) => {
          if (!active) return;
          setState({ error: error instanceof Error ? error : new Error(String(error)) });
        });
      return () => {
        active = false;
      };
    }, [revision]);

    let content: ReactNode = createElement(GlobalLoading);
    if (state.error) {
      content = createElement(RemoteLoadFailure, { error: state.error });
    } else if (state.component) {
      content = createElement(state.component, props);
    }

    return createElement(
      RemoteBoundary,
      {
        onRetry: () => {
          // ErrorBoundary 清除自身错误后会立即重渲染子树，因此这里同步清掉旧加载错误，避免旧异常再次把边界打回失败态。
          setState({});
          retry();
        },
      },
      content,
    );
  };
}
